import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useMutation,
  useQueryClient,
  type InfiniteData,
  type QueryKey,
} from "@tanstack/react-query";
import {
  fetchOpportunities,
  fetchOpportunityFacets,
  fetchOpportunityCreatives,
  fetchScripts,
  fetchScriptsFacets,
  toggleScriptFavorite,
  reorderScriptScenes,
  aiEditScripts,
  generateStoryboard,
  revertScript,
  resolveReferenceLinks,
  toOpportunityListSortBy,
  type FetchOpportunitiesParams,
  type FetchScriptsResponse,
  type OpportunityFacets,
  type OpportunityCreativesResponse,
  type ScriptDto,
  type ScriptScene,
  type ScriptsFacets,
  type ScriptsFilters,
  type ResolvedLinkResult,
  type StoryboardVariant,
} from "@/features/opportunities/api/opportunities.api";
import { fetchBulkJobStatus } from "@/features/opportunities/api/opportunities.api";
import { ApiError, getLocalUserFacingApiMessage } from "@/lib/api-client";
import { useAmberMutation } from "@/lib/use-amber-mutation";
import { toast } from "sonner";
import type { BulkJobStatusResponse } from "@nexus/contracts";
import { revealGenerationTask } from "./opportunity-sets.queries";

// ─── Key Factory ────────────────────────────────────────────────────

export const opportunityKeys = {
  all: ["opportunities"] as const,
  lists: () => ["opportunities", "list"] as const,
  list: (params: OpportunitiesListParams) =>
    ["opportunities", "list", params] as const,
  facets: (setId: string) => ["opportunities", "facets", setId] as const,
  bulkStatus: (correlationId: string) =>
    ["opportunities", "bulk-status", correlationId] as const,
  /** `pollStartedAt` is wall-clock ms when polling began; included so each export session has a stable max-duration window. */
  exportStatus: (correlationId: string, pollStartedAt = 0) =>
    ["opportunities", "export-status", correlationId, pollStartedAt] as const,
  creativesInfinite: (opportunityId: string, pageSize: number) =>
    ["opportunities", "creatives-infinite", opportunityId, pageSize] as const,
  /** Broad prefix used for invalidation — matches all scripts queries (lists + facets) for a set. */
  scripts: (setId: string) => ["opportunities", "scripts", setId] as const,
  /** Specific cache key per (search, filters, pageSize) combination. */
  scriptsList: (params: ScriptsListParams) =>
    [
      "opportunities",
      "scripts",
      params.setId,
      "list",
      { search: params.search, filters: params.filters, pageSize: params.pageSize },
    ] as const,
  scriptsFacets: (setId: string) =>
    ["opportunities", "scripts", setId, "facets"] as const,
  /** Per-(set, script) key for a storyboard generation action. */
  storyboard: (setId: string, scriptId: string) =>
    ["opportunities", "scripts", setId, "storyboard", scriptId] as const,
} as const;

// ─── Types ──────────────────────────────────────────────────────────

export interface OpportunitiesListFilters {
  action?: string;
  dataset?: string;
  feature?: string;
  subFeature?: string;
  engine?: string;
}

export interface OpportunitiesListParams {
  setId?: string;
  tab: string;
  filters: OpportunitiesListFilters;
  search: string;
  sort: { id: string; desc: boolean } | null;
  pageSize?: number;
}

export interface ScriptsListParams {
  setId: string;
  search: string;
  filters: ScriptsFilters;
  pageSize: number;
}

// ─── Query Options ──────────────────────────────────────────────────

export function opportunitiesInfiniteQueryOptions(
  params: OpportunitiesListParams & { enabled?: boolean },
) {
  const { setId, tab, filters, search, sort, pageSize = 20, enabled = true } = params;

  return infiniteQueryOptions({
    queryKey: opportunityKeys.list({ setId, tab, filters, search, sort, pageSize }),
    queryFn: ({ pageParam }) => {
      if (!setId) {
        return Promise.resolve({ items: [], nextCursor: null, totalCount: 0 });
      }
      // Strip sentinel "all" values — the backend treats absent params as "no filter",
      // so omitting them avoids sending noise and deduplicate cache slots.
      const cleanedFilters = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== undefined && v !== "all"),
      ) as Partial<OpportunitiesListFilters>;
      const dataset = cleanedFilters.dataset;
      const apiParams: FetchOpportunitiesParams = {
        setId,
        cursor: pageParam,
        pageSize,
        tab: tab !== "all" ? tab : undefined,
        search: search || undefined,
        sortBy: toOpportunityListSortBy(sort?.id),
        sortOrder: sort?.desc ? "desc" : "asc",
        ...cleanedFilters,
        ...(dataset ? { flowName: dataset } : {}),
      };
      return fetchOpportunities(apiParams);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: enabled && !!setId,
    placeholderData: keepPreviousData,
  });
}

const STALE_TIME_FACETS = 30_000;

export interface OpportunityFacetsParams {
  setId: string | undefined;
  tab?: string;
  dataset?: string;
}

export function opportunityFacetsQueryOptions(params: OpportunityFacetsParams | string | undefined) {
  // Accept legacy string (setId-only) calls for backwards compatibility.
  const resolved: OpportunityFacetsParams =
    typeof params === "string" ? { setId: params } : (params ?? { setId: undefined });
  const { setId, tab, dataset } = resolved;
  const cleanDataset = dataset && dataset !== "all" ? dataset : undefined;
  return queryOptions<OpportunityFacets>({
    queryKey: [...opportunityKeys.facets(setId ?? ""), { tab, dataset: cleanDataset }] as const,
    queryFn: () => fetchOpportunityFacets({ setId: setId!, tab, dataset: cleanDataset }),
    enabled: !!setId,
    staleTime: STALE_TIME_FACETS,
  });
}

const CREATIVES_POLL_INTERVAL_MS = 3_000;

/**
 * Infinite query for opportunity creatives — reads pre-cached rows persisted
 * at set-generation time. Polls every 3s while creativesStatus === "LOADING"
 * so the drawer reflects READY/FAILED without a manual refresh.
 */
export function opportunityCreativesInfiniteQueryOptions(
  opportunityId: string,
  pageSize = 24,
) {
  return infiniteQueryOptions<OpportunityCreativesResponse, Error, { pages: OpportunityCreativesResponse[]; pageParams: (string | undefined)[] }, ReturnType<typeof opportunityKeys.creativesInfinite>, string | undefined>({
    queryKey: opportunityKeys.creativesInfinite(opportunityId, pageSize),
    queryFn: ({ pageParam }) =>
      fetchOpportunityCreatives({
        opportunityId,
        pageSize,
        cursor: pageParam,
      }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore && lastPage.nextCursor ? lastPage.nextCursor : undefined,
    refetchInterval: (query) => {
      const status = query.state.data?.pages[0]?.creativesStatus;
      return status === "LOADING" ? CREATIVES_POLL_INTERVAL_MS : false;
    },
    // Keep a sliding window of pages so the JS heap stays bounded as the user
    // scrolls deep into a long creative list. Virtualization already bounds
    // DOM/GPU memory; this caps the raw item objects too.
    maxPages: 5,
    enabled: !!opportunityId,
    placeholderData: keepPreviousData,
    meta: { skipGlobalError: true },
  });
}

function isTerminalStatus(status: string | undefined): boolean {
  return status === "COMPLETED" || status === "FAILED";
}

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_DURATION_MS = 5 * 60 * 1000;
const EXPORT_MAX_POLL_DURATION_MS = 10 * 60 * 1000;

/** Polling query for export job status. Uses a 10-minute timeout (vs 5 min for bulk actions). */
export function exportStatusQueryOptions(correlationId: string, pollStartedAt: number) {
  return queryOptions<BulkJobStatusResponse | null>({
    queryKey: opportunityKeys.exportStatus(correlationId, pollStartedAt),
    queryFn: () => fetchBulkJobStatus(correlationId),
    enabled: !!correlationId && pollStartedAt > 0,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!pollStartedAt) return false;
      if (!data) return POLL_INTERVAL_MS;
      if (isTerminalStatus(data.status)) return false;
      const elapsed = Date.now() - pollStartedAt;
      if (elapsed > EXPORT_MAX_POLL_DURATION_MS) return false;
      return POLL_INTERVAL_MS;
    },
    retry: 3,
    retryDelay: 1000,
  });
}

/**
 * Cursor-paginated scripts query for the Completed Scripts tab.
 *
 * The key embeds search/filters/pageSize so each filter combination has its
 * own cache slot — combined with `keepPreviousData`, changing a filter swaps
 * to the new key without flashing empty state while the new pages load.
 *
 * `opportunityKeys.scripts(setId)` is the broader prefix that matches every
 * variant for the set (and the facets query) — use it for invalidation when
 * something outside the toolbar changes (e.g., AI edit completion).
 */
export function scriptsInfiniteQueryOptions(params: ScriptsListParams) {
  return infiniteQueryOptions<
    FetchScriptsResponse,
    Error,
    { pages: FetchScriptsResponse[]; pageParams: (string | undefined)[] },
    ReturnType<typeof opportunityKeys.scriptsList>,
    string | undefined
  >({
    queryKey: opportunityKeys.scriptsList(params),
    queryFn: ({ pageParam }) =>
      fetchScripts({
        setId: params.setId,
        cursor: pageParam,
        limit: params.pageSize,
        search: params.search || undefined,
        filters: params.filters,
      }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: !!params.setId,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function scriptsFacetsQueryOptions(setId: string | undefined) {
  return queryOptions<ScriptsFacets>({
    queryKey: opportunityKeys.scriptsFacets(setId ?? ""),
    queryFn: () => fetchScriptsFacets(setId!),
    enabled: !!setId,
    staleTime: STALE_TIME_FACETS,
  });
}

// ─── Mutations ──────────────────────────────────────────────────────

type InfiniteScripts = InfiniteData<FetchScriptsResponse, string | undefined>;
type ScriptsListCachePrefix = readonly ["opportunities", "scripts", string, "list"];

/**
 * Snapshot every cached `scriptsInfiniteQuery` variant for this set so we can
 * apply an optimistic update across all of them (e.g., the "Favorites Only"
 * tab AND the unfiltered list both render the same script row).
 */
function snapshotScriptsListQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  setId: string,
): { prefix: ScriptsListCachePrefix; entries: [QueryKey, InfiniteScripts | undefined][] } {
  const prefix = ["opportunities", "scripts", setId, "list"] as const;
  const entries = queryClient.getQueriesData<InfiniteScripts>({ queryKey: prefix });
  return { prefix, entries };
}

function applyToAllPages(
  queryClient: ReturnType<typeof useQueryClient>,
  entries: [QueryKey, InfiniteScripts | undefined][],
  updateItem: (s: ScriptDto) => ScriptDto,
) {
  for (const [key, data] of entries) {
    if (!data) continue;
    queryClient.setQueryData<InfiniteScripts>(key, {
      ...data,
      pages: data.pages.map((page) => ({
        ...page,
        items: page.items.map(updateItem),
      })),
    });
  }
}

interface ToggleFavoriteContext {
  entries: [QueryKey, InfiniteScripts | undefined][];
}

/**
 * Optimistically flips `isFavorite` on a single script across every cached
 * `scriptsInfiniteQuery` variant. Rolls back on error and revalidates the
 * whole `scripts(setId)` prefix on settle so the server stays authoritative
 * (including the facets cache, which may not change but is cheap to refresh).
 */
export function useToggleScriptFavorite(setId: string) {
  const queryClient = useQueryClient();
  const invalidatePrefix = opportunityKeys.scripts(setId);

  return useMutation<void, Error, { scriptId: string; isFavorite: boolean }, ToggleFavoriteContext>({
    mutationFn: ({ scriptId, isFavorite }) =>
      toggleScriptFavorite(setId, scriptId, isFavorite),
    onMutate: async ({ scriptId, isFavorite }) => {
      const { prefix, entries } = snapshotScriptsListQueries(queryClient, setId);
      await queryClient.cancelQueries({ queryKey: prefix });
      applyToAllPages(queryClient, entries, (s) =>
        s.id === scriptId ? { ...s, isFavorite } : s,
      );
      return { entries };
    },
    onError: (_err, _vars, context) => {
      if (!context) return;
      for (const [key, data] of context.entries) {
        // Skip variants that hadn't loaded at snapshot time — calling
        // setQueryData(key, undefined) would clobber any value the cache may
        // have acquired since (e.g., a tab the user switched to mid-flight).
        if (!data) continue;
        queryClient.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: invalidatePrefix });
    },
  });
}

interface ReorderScenesContext {
  entries: [QueryKey, InfiniteScripts | undefined][];
}

export function useReorderScriptScenes(setId: string) {
  const queryClient = useQueryClient();
  const invalidatePrefix = opportunityKeys.scripts(setId);

  return useMutation<void, Error, { scriptId: string; sceneIds: string[] }, ReorderScenesContext>({
    mutationFn: ({ scriptId, sceneIds }) => reorderScriptScenes(setId, scriptId, sceneIds),
    onMutate: async ({ scriptId, sceneIds }) => {
      const { prefix, entries } = snapshotScriptsListQueries(queryClient, setId);
      await queryClient.cancelQueries({ queryKey: prefix });
      applyToAllPages(queryClient, entries, (s) => {
        if (s.id !== scriptId) return s;
        const sceneMap = new Map(s.scenes.map((sc) => [sc.id, sc]));
        const orderedScenes = sceneIds
          .map((id, i) => {
            const sc = sceneMap.get(id);
            return sc ? ({ ...sc, scene_number: i + 1 } satisfies ScriptScene) : null;
          })
          .filter((sc): sc is ScriptScene => sc != null);
        return { ...s, scenes: orderedScenes };
      });
      return { entries };
    },
    onError: (_err, _vars, context) => {
      if (!context) return;
      for (const [key, data] of context.entries) {
        if (!data) continue;
        queryClient.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: invalidatePrefix });
    },
  });
}

/** Polling query for bulk job status. Stops polling when the job reaches a terminal state or times out. */
export function bulkJobStatusQueryOptions(correlationId: string) {
  return queryOptions<BulkJobStatusResponse | null>({
    queryKey: opportunityKeys.bulkStatus(correlationId),
    queryFn: () => fetchBulkJobStatus(correlationId),
    enabled: !!correlationId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return POLL_INTERVAL_MS;
      if (isTerminalStatus(data.status)) return false;
      const elapsed = Date.now() - query.state.dataUpdatedAt;
      if (elapsed > MAX_POLL_DURATION_MS) return false;
      return POLL_INTERVAL_MS;
    },
    retry: 3,
    retryDelay: 1000,
  });
}

// ─── AI Edit Scripts (Amber Path) ───────────────────────────────────

const AI_EDIT_POLL_MAX_ATTEMPTS = 60;
const AI_EDIT_POLL_INTERVAL_MS = 1_000;

interface AiEditScriptsInput {
  setId: string;
  scriptIds: string[];
  instruction: string;
  tenantId: string;
}

/**
 * Amber Path mutation: POST ai-edit → correlationId → poll until terminal.
 * On success, invalidates cached script queries so cards reflect the new version.
 */
export function useAiEditScripts() {
  const queryClient = useQueryClient();

  return useAmberMutation<AiEditScriptsInput>({
    mutationFn: ({ setId, scriptIds, instruction, tenantId }) =>
      aiEditScripts(setId, scriptIds, instruction, tenantId),
    pollBasePath: "/opportunity-sets",
    invalidateKeys: [],
    onAccepted: () => {
      revealGenerationTask(queryClient);
    },
    onSuccess: (_result, variables) => {
      // Invalidate all script queries for this set so fresh data is fetched
      void queryClient.invalidateQueries({
        queryKey: opportunityKeys.scripts(variables.setId),
      });
    },
    maxAttempts: AI_EDIT_POLL_MAX_ATTEMPTS,
    intervalMs: AI_EDIT_POLL_INTERVAL_MS,
  });
}

// The "Suggest Script" async flow lives in `useSuggestScriptsJob` (feature
// hooks) and reports progress through the unified task-progress sidebar.
// Its POST helper is `suggestScripts` in the opportunities API client.

// ─── Resolve Reference Links (Amber Path) ───────────────────────────

const RESOLVE_LINKS_POLL_MAX_ATTEMPTS = 30;
const RESOLVE_LINKS_POLL_INTERVAL_MS = 500;

interface ResolveReferenceLinksInput {
  setId: string;
  referenceUrls: string[];
  tenantId: string;
}

/**
 * Amber Path mutation: POST scripts/resolve-links → correlationId → poll until
 * terminal. Used by the Suggest Script modal as a distinct "fetch links first"
 * step — the terminal status's `results` array carries per-URL `{ url, ok,
 * reason }` so the modal can gate generation on the outcome. No success toast:
 * the modal drives its own UX (proceed / review-and-choose).
 */
export function useResolveReferenceLinks() {
  return useAmberMutation<ResolveReferenceLinksInput>({
    mutationFn: ({ setId, referenceUrls, tenantId }) =>
      resolveReferenceLinks(setId, referenceUrls, tenantId),
    pollBasePath: "/opportunity-sets",
    maxAttempts: RESOLVE_LINKS_POLL_MAX_ATTEMPTS,
    intervalMs: RESOLVE_LINKS_POLL_INTERVAL_MS,
  });
}

export function toResolvedLinkResults(result: unknown): ResolvedLinkResult[] {
  const results = (result as { results?: unknown } | null)?.results;
  return Array.isArray(results) ? (results as ResolvedLinkResult[]) : [];
}

// ─── Generate Storyboard (Amber Path) ───────────────────────────────

const STORYBOARD_POLL_MAX_ATTEMPTS = 60;
const STORYBOARD_POLL_INTERVAL_MS = 1_000;

interface GenerateStoryboardInput {
  scriptId: string;
  tenantId: string;
  /** Omitted means the sketch sheet, matching the API default. */
  variant?: StoryboardVariant;
}

/**
 * Amber Path mutation: POST storyboard → correlationId → poll until terminal.
 * On settle, invalidates cached script queries for the set so the affected
 * card reflects the new `storyboardStatus` / `storyboardImages`, or the
 * `storyboardPanelStatus` / `storyboardPanels` of the photoreal variant.
 */
export function useGenerateStoryboard(setId: string) {
  const queryClient = useQueryClient();

  return useAmberMutation<GenerateStoryboardInput>({
    mutationFn: ({ scriptId, tenantId, variant }) =>
      generateStoryboard(setId, scriptId, tenantId, variant),
    pollBasePath: "/opportunity-sets",
    successMessage: "Storyboard generated successfully",
    invalidateKeys: [],
    onSuccess: () => {
      // Invalidate all script queries for this set so fresh storyboard data is fetched.
      void queryClient.invalidateQueries({
        queryKey: opportunityKeys.scripts(setId),
      });
    },
    maxAttempts: STORYBOARD_POLL_MAX_ATTEMPTS,
    intervalMs: STORYBOARD_POLL_INTERVAL_MS,
  });
}

// ─── Revert Script (Sync) ───────────────────────────────────────────

interface RevertScriptInput {
  setId: string;
  scriptId: string;
}

/**
 * Synchronous mutation: reverts a script's scenes to the previous version.
 * On success, invalidates cached script queries and shows a toast.
 * On 409 (no previous version), shows a warning toast.
 */
export function useRevertScript(setId: string) {
  const queryClient = useQueryClient();

  return useMutation<ScriptDto, Error, RevertScriptInput>({
    mutationFn: ({ setId: sid, scriptId }) => revertScript(sid, scriptId),
    onSuccess: () => {
      toast.success("Script reverted successfully");
    },
    onError: (err) => {
      const status = err instanceof ApiError ? err.status : undefined;
      if (status === 409) {
        toast.warning("No previous version available");
      } else {
        const message = getLocalUserFacingApiMessage(err);
        if (message) toast.error(message);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: opportunityKeys.scripts(setId),
      });
    },
    meta: { skipGlobalError: true },
  });
}
